import { useCallback, useEffect, useRef, useState } from 'react'
import { Ghost, Glasses, PaintBucket, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import { viewerApi } from '@viewer/lib/viewerApi'
import { paintCustomColor, resetCustomColor } from '@viewer/lib/classColors'

const W = 280
const PRESETS = [
  '#dc2626', '#ea580c', '#facc15', '#65a30d',
  '#0891b2', '#2563eb', '#7c3aed', '#db2777',
  '#0a0a0a', '#525252', '#a3a3a3', '#ffffff',
]

/**
 * Draggable "Aparência" panel housing the heavier per-element
 * styling controls (paint, ghost slider, transparency slider).
 * Lives outside the ribbon so the user can keep it open while
 * clicking elements in the canvas. Only one instance ever; a
 * single open flag in the store toggles visibility.
 */
function FloatingAppearancePanel() {
  const open = useAppStore((s) => s.appearancePanelOpen)
  const setOpen = useAppStore((s) => s.setAppearancePanelOpen)
  const { z, trazer } = useJanelaAberta('aparencia', open)
  const position = useAppStore((s) => s.appearancePanelPosition)
  const setPosition = useAppStore((s) => s.setAppearancePanelPosition)
  const selectedTargets = useAppStore((s) => s.selectedTargets)
  const ghostMode = useAppStore((s) => s.ghostMode)
  const ghostOpacity = useAppStore((s) => s.ghostOpacity)
  const transparentCount = useAppStore((s) => s.transparentCount)
  const transparencyOpacity = useAppStore((s) => s.transparencyOpacity)
  const [color, setColor] = useState('#dc2626')
  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { x: position.x, y: position.y },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [position.x, position.y],
  )

  useEffect(() => {
    if (!open) return
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return
      const { pointerStart, posStart } = dragRef.current
      const dx = e.clientX - pointerStart.x
      const dy = e.clientY - pointerStart.y
      const x = Math.min(
        Math.max(0, posStart.x + dx),
        window.innerWidth - 80,
      )
      const y = Math.min(
        Math.max(0, posStart.y + dy),
        window.innerHeight - 40,
      )
      setPosition({ x, y })
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [open, setPosition])

  if (!open) return null
  const selectedCount = selectedTargets.length
  const ghostPct = Math.round(ghostOpacity * 100)
  const tpct = Math.round(transparencyOpacity * 100)

  const applyPaint = (hex: string) => {
    const byModel = new Map<string, number[]>()
    for (const t of selectedTargets) {
      const arr = byModel.get(t.modelId) ?? []
      arr.push(t.localId)
      byModel.set(t.modelId, arr)
    }
    for (const [modelId, ids] of byModel) {
      const fm = viewerApi.getModel(modelId)
      if (fm) void paintCustomColor(fm, ids, hex)
    }
  }
  const resetPaint = () => {
    const byModel = new Map<string, number[]>()
    for (const t of selectedTargets) {
      const arr = byModel.get(t.modelId) ?? []
      arr.push(t.localId)
      byModel.set(t.modelId, arr)
    }
    for (const [modelId, ids] of byModel) {
      const fm = viewerApi.getModel(modelId)
      if (fm) void resetCustomColor(fm, ids)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Aparência"
      onPointerDownCapture={trazer}
      className="fixed flex flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-xs shadow-2xl"
      style={{ zIndex: z, left: position.x, top: position.y, width: W }}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 select-none"
      >
        <span className="font-medium text-[var(--color-text)]">Aparência</span>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(false)}
          className="rounded px-1.5 py-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          <X size={12} />
        </button>
      </div>

      <div className="flex flex-col gap-3 p-3">
        {/* Pintar selecção */}
        <Section icon={<PaintBucket size={12} />} title="Pintar selecção">
          <div className="text-[10px] text-[var(--color-text-muted)]">
            {selectedCount === 0
              ? 'Sem seleção. Selecciona um ou mais elementos.'
              : `${selectedCount} selecionado${selectedCount === 1 ? '' : 's'}`}
          </div>
          <div className="grid grid-cols-6 gap-1">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setColor(p)
                  applyPaint(p)
                }}
                disabled={selectedCount === 0}
                title={p}
                className="h-6 w-6 rounded border border-[var(--color-border)] transition-transform hover:scale-110 disabled:opacity-40"
                style={{ backgroundColor: p }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={selectedCount === 0}
              className="h-6 w-12 cursor-pointer rounded border border-[var(--color-border)] bg-transparent disabled:opacity-40"
            />
            <button
              type="button"
              onClick={() => applyPaint(color)}
              disabled={selectedCount === 0}
              className="rounded bg-[var(--color-accent)] px-2 py-0.5 font-medium text-[var(--color-bg)] hover:opacity-90 disabled:opacity-40"
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={resetPaint}
              disabled={selectedCount === 0}
              className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
            >
              Repor
            </button>
          </div>
        </Section>

        {/* Transparência */}
        <Section icon={<Glasses size={12} />} title="Transparência">
          <div className="text-[10px] text-[var(--color-text-muted)]">
            {transparentCount > 0
              ? `${transparentCount} elementos com transparência aplicada`
              : 'Sem elementos transparentes'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void viewerApi.makeTransparent()}
              disabled={selectedCount === 0}
              className="rounded bg-[var(--color-accent)] px-2 py-0.5 text-[var(--color-bg)] hover:opacity-90 disabled:opacity-40"
            >
              Aplicar à selecção
            </button>
            <button
              type="button"
              onClick={() => void viewerApi.clearTransparent()}
              disabled={transparentCount === 0}
              className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
            >
              Repor
            </button>
          </div>
          <label className="flex items-center gap-2">
            <span className="w-16 text-[10px] text-[var(--color-text-muted)]">
              Opacidade
            </span>
            <input
              type="range"
              min={5}
              max={90}
              value={tpct}
              onChange={(e) =>
                void viewerApi.setTransparencyOpacity(Number(e.target.value) / 100)
              }
              className="flex-1"
            />
            <span className="w-8 text-right font-mono text-[10px] text-[var(--color-text)]">
              {tpct}%
            </span>
          </label>
        </Section>

        {/* Ghost mode */}
        <Section icon={<Ghost size={12} />} title="Ghost (contexto)">
          <div className="text-[10px] text-[var(--color-text-muted)]">
            Não-selecionados ficam translúcidos cinza para destacar a selecção.
          </div>
          <button
            type="button"
            onClick={() => void viewerApi.setGhostMode(!ghostMode)}
            className={`rounded px-2 py-0.5 ${
              ghostMode
                ? 'bg-[var(--color-accent)] text-[var(--color-bg)]'
                : 'border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {ghostMode ? 'Ghost ON' : 'Ghost OFF'}
          </button>
          <label className="flex items-center gap-2">
            <span className="w-16 text-[10px] text-[var(--color-text-muted)]">
              Opacidade
            </span>
            <input
              type="range"
              min={2}
              max={50}
              value={ghostPct}
              disabled={!ghostMode}
              onChange={(e) =>
                void viewerApi.setGhostOpacity(Number(e.target.value) / 100)
              }
              className="flex-1 disabled:opacity-40"
            />
            <span className="w-8 text-right font-mono text-[10px] text-[var(--color-text)]">
              {ghostPct}%
            </span>
          </label>
        </Section>
      </div>
    </div>
  )
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 p-2">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

export default FloatingAppearancePanel
